import { useEffect, useCallback, useRef, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { useAuctionStore } from '../stores/auctionStore';
import { auctionService } from '../services/auctionService';
const POLL_INTERVAL = 5000;
const RECONNECT_DELAYS = [0, 2000, 5000, 10000, 30000];
export const useAuctionLive = (auctionId) => {
    const { currentAuction, currentSummary, currentBids, isConnected, reconnecting, outbidAlert, winAlert, setCurrentAuction, updateSummary, addRecentBid, setConnectionState, setOutbidAlert, setWinAlert, clearCurrentAuction, } = useAuctionStore();
    const connectionRef = useRef(null);
    const pollRef = useRef(null);
    const [isJoined, setIsJoined] = useState(false);
    const [useSignalR, setUseSignalR] = useState(true);
    // Join auction - fetch initial state via REST
    const joinAuction = useCallback(async () => {
        if (!auctionId)
            return;
        try {
            const result = await auctionService.watchAuction(auctionId, 'join');
            if (result.success && result.data) {
                const detail = await auctionService.getAuctionDetail(auctionId);
                if (detail.success) {
                    setCurrentAuction(detail.data.auction, detail.data.summary, detail.data.bids);
                }
                setIsJoined(true);
            }
        }
        catch (err) {
            console.error('Failed to join auction:', err);
        }
    }, [auctionId, setCurrentAuction]);
    // Leave auction
    const leaveAuction = useCallback(async () => {
        if (!auctionId)
            return;
        try {
            await auctionService.watchAuction(auctionId, 'leave');
            setIsJoined(false);
            clearCurrentAuction();
        }
        catch (err) {
            console.error('Failed to leave auction:', err);
        }
    }, [auctionId, clearCurrentAuction]);
    // Place bid
    const placeBid = useCallback(async (amount) => {
        if (!auctionId)
            return { success: false, error: 'No auction selected' };
        try {
            const result = await auctionService.placeBid(auctionId, amount);
            return result;
        }
        catch (err) {
            return { success: false, error: err.message || 'Failed to place bid' };
        }
    }, [auctionId]);
    // Refresh summary (reconnection recovery / polling fallback)
    const refreshSummary = useCallback(async () => {
        if (!auctionId)
            return;
        try {
            const result = await auctionService.getSummary(auctionId);
            if (result.success) {
                updateSummary(result.data.summary);
            }
        }
        catch (err) {
            console.error('Failed to refresh summary:', err);
        }
    }, [auctionId, updateSummary]);
    // Start polling fallback
    const startPolling = useCallback(() => {
        if (pollRef.current)
            return;
        pollRef.current = setInterval(() => {
            if (isJoined) {
                refreshSummary();
            }
        }, POLL_INTERVAL);
    }, [isJoined, refreshSummary]);
    // Stop polling
    const stopPolling = useCallback(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, []);
    // Setup SignalR connection
    useEffect(() => {
        if (!auctionId || !useSignalR)
            return;
        let disposed = false;
        const setupConnection = async () => {
            try {
                // Negotiate to get SignalR connection info
                const negotiation = await auctionService.negotiateSignalR();
                if (disposed || !negotiation?.url) {
                    // SignalR not available, fall back to polling
                    setUseSignalR(false);
                    return;
                }
                const connection = new signalR.HubConnectionBuilder()
                    .withUrl(negotiation.url, {
                    accessTokenFactory: () => negotiation.accessToken || '',
                })
                    .withAutomaticReconnect(RECONNECT_DELAYS)
                    .configureLogging(signalR.LogLevel.Warning)
                    .build();
                // Event handlers
                connection.on('new_bid', (event) => {
                    addRecentBid({
                        anonymousId: event.anonymousId,
                        amount: event.amount,
                        timestamp: new Date().toISOString(),
                    });
                    updateSummary({
                        currentBid: event.amount,
                        currentBidderAnonymousId: event.anonymousId,
                        bidCount: event.bidCount,
                        endTime: event.endTime,
                        graceExtended: event.graceExtended,
                    });
                });
                connection.on('you_were_outbid', (event) => {
                    setOutbidAlert(event);
                });
                connection.on('you_won', (event) => {
                    setWinAlert(event);
                });
                connection.on('auction_closed', (event) => {
                    updateSummary({
                        status: 'closed',
                        finalPrice: event.finalPrice,
                        winnerAnonymousId: event.winnerAnonymousId || undefined,
                        reserveMet: event.reserveMet,
                    });
                });
                connection.on('supervised_state', (event) => {
                    updateSummary({
                        supervisedState: event.state,
                        currentBid: event.currentBid,
                        bidCount: event.bidCount,
                        status: event.auctionStatus,
                    });
                });
                // Connection lifecycle
                connection.onreconnecting(() => {
                    if (!disposed) {
                        setConnectionState(false, true);
                        startPolling(); // Fall back to polling during reconnection
                    }
                });
                connection.onreconnected(() => {
                    if (!disposed) {
                        stopPolling();
                        setConnectionState(true);
                        refreshSummary(); // Recover any missed events
                    }
                });
                connection.onclose(() => {
                    if (!disposed) {
                        setConnectionState(false);
                        startPolling(); // Fall back to polling when disconnected
                    }
                });
                // Start connection
                await connection.start();
                if (!disposed) {
                    connectionRef.current = connection;
                    setConnectionState(true);
                    stopPolling(); // SignalR is live, no need for polling
                }
                else {
                    await connection.stop();
                }
            }
            catch (err) {
                console.warn('SignalR connection failed, falling back to polling:', err);
                if (!disposed) {
                    setUseSignalR(false);
                }
            }
        };
        setupConnection();
        return () => {
            disposed = true;
            if (connectionRef.current) {
                connectionRef.current.stop();
                connectionRef.current = null;
            }
        };
    }, [auctionId, useSignalR]);
    // Polling fallback when SignalR is unavailable
    useEffect(() => {
        if (!auctionId || useSignalR)
            return;
        setConnectionState(true); // Polling is a valid "connection"
        startPolling();
        return () => {
            stopPolling();
            setConnectionState(false);
        };
    }, [auctionId, useSignalR, isJoined]);
    // Auto-join on mount
    useEffect(() => {
        if (auctionId && !isJoined) {
            joinAuction();
        }
        return () => {
            if (isJoined) {
                leaveAuction();
            }
        };
    }, [auctionId]);
    // Cleanup polling on unmount
    useEffect(() => {
        return () => stopPolling();
    }, [stopPolling]);
    // Computed values
    const minimumBid = currentSummary
        ? currentSummary.bidCount === 0
            ? currentAuction?.startingPrice || 0
            : Math.ceil(currentSummary.currentBid * (1 + (currentAuction?.minimumIncrementPercent || 2) / 100))
        : 0;
    const timeRemaining = currentSummary?.endTime
        ? Math.max(0, new Date(currentSummary.endTime).getTime() - Date.now())
        : null;
    return {
        // State
        auction: currentAuction,
        summary: currentSummary,
        recentBids: currentSummary?.recentBids || [],
        auctionStatus: currentSummary?.status || currentAuction?.status || null,
        supervisedState: currentSummary?.supervisedState || null,
        // Computed
        minimumBid,
        timeRemaining,
        // Notifications
        outbidAlert,
        winAlert,
        clearOutbidAlert: () => setOutbidAlert(null),
        clearWinAlert: () => setWinAlert(null),
        // Actions
        placeBid,
        joinAuction,
        leaveAuction,
        refreshSummary,
        // Connection
        isConnected,
        reconnecting,
        isJoined,
    };
};
